import { Group, Menu, Stack } from '@mantine/core';
import type { AiTaskOut, SecodaAIReferencedObjectOut } from '@repo/api-codegen';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { UserRole } from '@repo/common/enums/UserRole';
import { useSyncStoreWithSearchParams } from '@repo/common/utils/useSyncStoreWithSearchParams';
import { Icon, IconButton, SegmentedControl, Text } from '@repo/foundations';
import { mapKeys, pickBy } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useAuthUser, useDeleteAllAIEmbedded } from '../../api';
import { AIChat } from '../../components/AIAssistant';
import { AIAssistantProvider } from '../../components/AIAssistant/context';
import { AIAssistantMode } from '../../components/AIAssistant/types';
import { EmptyState } from '../../components/EmptyState';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { openDeleteConfirmModal } from '../../components/ModalManager';
import {
	PageLayoutContentWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import {
	rightSidebarStore,
	RightSidebarWrapper,
} from '../../components/RightSidebar';
import { RightSideCanvasWrapper } from '../../components/RightSideCanvas/RightSideCanvasWrapper';
import { RightSideCanvasStore } from '../../components/RightSideCanvas/store';
import AIPageNavBar from '../../components/SecodaAppShell/SecodaNavBar/AIPageNavBar';
import { AiAgentsController } from '../../features/AiAgents/AiAgentsController';
import { AiChatController } from '../../features/AiChat';
import { AiRightSideDisplay } from '../../features/AiChat/components/AiRightSideDisplay';
import { AiChatHistoryController } from '../../features/AiChatHistory';
import type { AiRightSideCanvasObject } from '../../features/AiReferencedObjectCard';
import { useAiWorkspaceSettingEnabled } from '../../hooks/useAiWorkspaceSettingEnabled';
import { isImpersonating } from '../../utils/cache/utils';
import { useFeatureFlags } from '../../utils/featureFlags';
import { CreateAgentModal } from '../AIAgentsPage/CreateAgentModal';

import { isValidAITab, type AITab } from './AIAssistantPage.utils';

function AIAssistantPage() {
	const { id } = useParams();
	const enableAi = useAiWorkspaceSettingEnabled();
	const { data: memberEnv } = useSuspenseApiMemberEnv({});
	const { user, workspace } = useAuthUser();
	const { aiNewArchitecture, secodaAiTasks } = useFeatureFlags();
	const { mutate: deleteAll } = useDeleteAllAIEmbedded();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	const tabParam = searchParams.get('tab');
	const tab: AITab = isValidAITab(tabParam) ? tabParam : 'chats';
	const [activeTab, setActiveTab] = useState<AITab>('chats');

	const rightSideCanvasStore = useMemo(
		() =>
			new RightSideCanvasStore<AiRightSideCanvasObject>({
				rightSidebarStore: rightSidebarStore,
			}),
		[id]
	);

	// Sync store.pageSideCanvasMode with search params to allow for navigating back and forth
	useSyncStoreWithSearchParams<
		typeof rightSideCanvasStore,
		typeof rightSideCanvasStore.pageSideCanvasMode
	>({
		store: rightSideCanvasStore,
		getter: useCallback(
			() => rightSideCanvasStore.pageSideCanvasMode,
			[rightSideCanvasStore]
		),
		setter: useCallback(
			(store, newState) => store.setPageSideCanvasMode(newState),
			[]
		),
		serialize: useCallback(
			(state) => ({
				canvas_mode: state.mode,
				...(state.mode === 'show'
					? mapKeys(
							state.objectToShow,
							(_, key) => `canvas_objectToShow_${key}`
						)
					: {}),
			}),
			[]
		),
		deserialize: useCallback(
			(params) => ({
				mode: (params.canvas_mode as 'hide' | 'show') || 'hide',
				objectToShow: mapKeys(
					pickBy(params, (_value, key) =>
						key.startsWith('canvas_objectToShow_')
					),
					(_value, key) => key.replace('canvas_objectToShow_', '')
				) as AiRightSideCanvasObject,
			}),
			[]
		),
	});

	const handleTabChange = useCallback(
		(newTab: AITab) => {
			const previousTab = activeTab;

			trackEvent(
				'ai/sidebar/tab_switch',
				{
					from_tab: previousTab,
					to_tab: newTab,
					mode: 'sidebar',
				},
				user,
				workspace
			);

			setActiveTab(newTab);
		},
		[activeTab, user, workspace]
	);

	type ChatFilter = 'all' | 'human' | 'agent';
	const [chatFilter, setChatFilter] = useState<ChatFilter>('all');

	const filterOptions = [
		{
			value: 'all' as ChatFilter,
			label: 'All chats',
			icon: 'message' as const,
		},
		{
			value: 'human' as ChatFilter,
			label: 'Human-triggered',
			icon: 'user' as const,
		},
		{
			value: 'agent' as ChatFilter,
			label: 'Agent-triggered',
			icon: 'topologyStar' as const,
		},
	];

	const handleDeleteAll = useCallback(() => {
		openDeleteConfirmModal({
			title: 'Are you sure you want to clear all conversations?',
			description: "You can't undo this action.",
			confirmProps: {
				variant: 'primary',
				tone: 'critical',
			},
			onConfirm: () => {
				trackEvent(
					'ai/history/delete_all',
					{
						mode: AIAssistantMode.PAGE,
					},
					user,
					workspace
				);
				deleteAll();
			},
			confirmLabel: 'Clear conversations',
		});
	}, [deleteAll, user, workspace]);

	const [modalOpened, setModalOpened] = useState(false);
	const [editingAgent, setEditingAgent] = useState<AiTaskOut | null>(null);

	const handleOpenAgentModal = useCallback((agent?: AiTaskOut) => {
		if (agent) {
			setEditingAgent(agent);
		} else {
			setEditingAgent(null);
		}
		setModalOpened(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalOpened(false);
		setEditingAgent(null);
	}, []);

	const onReferencedObjectClick = useCallback(
		(referencedObject: SecodaAIReferencedObjectOut) => {
			const isShowingThisObject =
				rightSideCanvasStore.pageSideCanvasMode.mode === 'show' &&
				((rightSideCanvasStore.pageSideCanvasMode.objectToShow.type ===
					'bi_metric' &&
					rightSideCanvasStore.pageSideCanvasMode.objectToShow.biMetricId ===
						referencedObject.object_id) ||
					(rightSideCanvasStore.pageSideCanvasMode.objectToShow.type ===
						'bi_dashboard' &&
						rightSideCanvasStore.pageSideCanvasMode.objectToShow
							.biDashboardId === referencedObject.object_id));

			if (isShowingThisObject) {
				rightSideCanvasStore.setPageSideCanvasMode({
					mode: 'hide',
				});
				return;
			}
			if ((referencedObject.object_type as string) === 'DOCUMENT') {
				navigate(`/docs/${referencedObject.object_id}`);
				return;
			}

			if (referencedObject.object_type === 'BI_METRIC') {
				rightSideCanvasStore.setPageSideCanvasMode({
					mode: 'show',
					objectToShow: {
						type: 'bi_metric',
						biMetricId: referencedObject.object_id,
					},
				});
			}

			if (referencedObject.object_type === 'BI_DASHBOARD') {
				rightSideCanvasStore.setPageSideCanvasMode({
					mode: 'show',
					objectToShow: {
						type: 'bi_dashboard',
						biDashboardId: referencedObject.object_id,
					},
				});
			}
		},
		[rightSideCanvasStore, navigate]
	);

	const shouldShowInlineReferencedObject = useCallback(
		(referencedObject: SecodaAIReferencedObjectOut) => {
			if (referencedObject.object_type !== 'BI_METRIC') {
				return false;
			}

			// Don't show inline if it's already in the right canvas
			const isAlreadyInRightCanvas =
				rightSideCanvasStore.pageSideCanvasMode.mode === 'show' &&
				rightSideCanvasStore.pageSideCanvasMode.objectToShow.type ===
					'bi_metric' &&
				rightSideCanvasStore.pageSideCanvasMode.objectToShow.biMetricId ===
					referencedObject.object_id;

			return !isAlreadyInRightCanvas;
		},
		[rightSideCanvasStore]
	);

	if (!enableAi) {
		return (
			<EmptyState
				illustrationName="upgrade"
				title="Secoda AI is disabled"
				description="Secoda AI is not enabled for this workspace. Please contact your administrator to turn on access."
				includeGoBack={false}
				stateHeight="80vh"
				size="lg"
			/>
		);
	}

	if (memberEnv?.AI_ENV_CONFIGURED === false) {
		return (
			<EmptyState
				illustrationName="upgrade"
				title="Secoda AI needs to be configured"
				description="Secoda AI requires an API key to be configured. Please contact your administrator to add one of the following environment variables: ANTHROPIC_API_KEY or XAI_API_KEY to the on-premise environment."
				includeGoBack={false}
				stateHeight="80vh"
				size="lg"
			/>
		);
	}

	return (
		<FeatureGuard
			featureName="Secoda AI"
			v1AllowedRoles={[
				UserRole.ADMIN,
				UserRole.EDITOR,
				UserRole.VIEWER,
				UserRole.GUEST,
			]}
			v2Permission="SecodaAI.Read"
			description="Use Secoda AI to easily access all your company data and find answers to common questions."
			isFeatureIncludedInPlan={enableAi}
			isAlwaysIncludedFeature={true}
		>
			<AIAssistantProvider mode={AIAssistantMode.PAGE} id={id ?? 'new'}>
				<PageLayoutWrapper name="ai-assistant">
					<PageLayoutContentWrapper name="ai-assistant">
						<AIPageNavBar
							rightSidebarStore={rightSidebarStore}
							forceHideRightSidebarControl={
								rightSideCanvasStore.pageSideCanvasMode.mode === 'show'
							}
						/>
						{aiNewArchitecture ? (
							<AiChatController
								initialId={!id || id === 'new' ? undefined : id}
								onReferencedObjectClick={onReferencedObjectClick}
								shouldShowInlineReferencedObject={
									shouldShowInlineReferencedObject
								}
							/>
						) : (
							<AIChat />
						)}
					</PageLayoutContentWrapper>
					<RightSideCanvasWrapper store={rightSideCanvasStore}>
						<AiRightSideDisplay store={rightSideCanvasStore} />
					</RightSideCanvasWrapper>
					{rightSideCanvasStore.pageSideCanvasMode.mode === 'hide' && (
						<RightSidebarWrapper>
							{secodaAiTasks ? (
								<Stack spacing="md" h="100%" mt={isImpersonating() ? 'lg' : 0}>
									<Stack w="100%" pl="md" pr="sm" my="sm">
										<SegmentedControl
											data={[
												{ label: 'Chats', value: 'chats' },
												{ label: 'Agents', value: 'agents' },
											]}
											value={activeTab}
											onChange={(val) => handleTabChange(val as AITab)}
											size="sm"
											fullWidth
										/>
									</Stack>
									{activeTab === 'chats' ? (
										<>
											<Group position="apart" ml="md" pl="3xs" pr="sm">
												<Text size="sm" fw={600}>
													Chat history
												</Text>
												<Group spacing={0}>
													<Menu position="bottom-end">
														<Menu.Target>
															<IconButton
																iconName="filter"
																variant="tertiary"
																tooltip="Filter"
															/>
														</Menu.Target>
														<Menu.Dropdown>
															<Menu.Label pl="xs" fw={600}>
																Filter
															</Menu.Label>
															{filterOptions.map((option) => {
																const isSelected = chatFilter === option.value;
																return (
																	<Menu.Item
																		key={option.value}
																		rightSection={
																			isSelected && <Icon name="check" />
																		}
																		sx={(theme) => ({
																			...(isSelected && {
																				backgroundColor: theme.other.getColor(
																					'fill/tertiary/default'
																				),
																				'&:hover': {
																					backgroundColor: theme.other.getColor(
																						'fill/tertiary/default'
																					),
																				},
																			}),
																		})}
																		onClick={() => setChatFilter(option.value)}
																	>
																		<Group spacing="2xs">
																			<Icon name={option.icon} />
																			<Text size="sm">{option.label}</Text>
																		</Group>
																	</Menu.Item>
																);
															})}
														</Menu.Dropdown>
													</Menu>
													<Menu>
														<Menu.Target>
															<IconButton
																iconName="dots"
																variant="tertiary"
																tooltip="More actions"
															/>
														</Menu.Target>
														<Menu.Dropdown>
															<Menu.Item onClick={handleDeleteAll}>
																<Group spacing="2xs">
																	<Icon name="trash" />
																	<Text size="sm">Delete all chats...</Text>
																</Group>
															</Menu.Item>
														</Menu.Dropdown>
													</Menu>
												</Group>
											</Group>
											<AiChatHistoryController filter={chatFilter} />
										</>
									) : (
										<>
											<Group position="apart" ml="md" pl="xs" pr="sm">
												<Text size="sm" fw={600}>
													Agents
												</Text>
												<IconButton
													onClick={() => handleOpenAgentModal()}
													variant="tertiary"
													iconName="plus"
												/>
											</Group>
											<AiAgentsController />
										</>
									)}
								</Stack>
							) : (
								<Stack spacing="md" h="100%" mt={isImpersonating() ? 'lg' : 0}>
									<Group position="apart" ml="md" pl="xs" pr="sm">
										<Text size="sm" fw={600}>
											Chat history
										</Text>
										<Menu>
											<Menu.Target>
												<IconButton iconName="dots" variant="tertiary" />
											</Menu.Target>
											<Menu.Dropdown>
												<Menu.Item onClick={handleDeleteAll}>
													<Group spacing="2xs">
														<Icon name="trash" />
														<Text size="sm">Delete all chats...</Text>
													</Group>
												</Menu.Item>
											</Menu.Dropdown>
										</Menu>
									</Group>
									<AiChatHistoryController filter={chatFilter} />
								</Stack>
							)}
						</RightSidebarWrapper>
					)}
				</PageLayoutWrapper>
			</AIAssistantProvider>
			{secodaAiTasks && modalOpened && (
				<CreateAgentModal onClose={handleCloseModal} agent={editingAgent} />
			)}
		</FeatureGuard>
	);
}

const ObservedAIAssistantPage = observer(AIAssistantPage);
export default ObservedAIAssistantPage;
